import { ObjectComparator, objectKeys } from '@ps/utils';

export interface HoistedProjectPayload<TRoot, TChild> {
  readonly root: TRoot;
  readonly children: TChild[];
}

// Moves fields identical on every child to root and strips them from each
// child — backend merges as `{ ...root, ...child }`. `exceptKeys` must list
// any field merged another way (e.g. quotas/qualifications, by
// `qualification_code`) and any per-child identity field.
export function hoistCommonFields<
  TChild extends Record<string, unknown>,
  TRoot extends Partial<TChild>,
>(
  root: TRoot,
  children: readonly TChild[],
  exceptKeys: readonly (keyof TChild)[],
): HoistedProjectPayload<TRoot, TChild> {
  const [first, ...rest] = children;
  if (!first || !rest.length) {
    return { root, children: [...children] };
  }

  const comparator = new ObjectComparator();
  const exceptions = new Set<keyof TChild>(exceptKeys);
  const hoistableKeys = objectKeys(first).filter(
    (key) =>
      !exceptions.has(key) &&
      children.every(
        (child) =>
          Object.prototype.hasOwnProperty.call(child, key) &&
          comparator.isEqual(child[key], first[key]),
      ),
  );

  if (!hoistableKeys.length) {
    return { root, children: [...children] };
  }

  const hoisted: Partial<TChild> = {};
  hoistableKeys.forEach((key) => {
    hoisted[key] = first[key];
  });

  const strippedChildren = children.map((child) => {
    const copy = { ...child };
    hoistableKeys.forEach((key) => delete copy[key]);
    return copy;
  });

  return { root: { ...root, ...hoisted }, children: strippedChildren };
}
